export interface Coordinates {
  lat: number;
  lng: number;
}

export interface GeocodeResult {
  coordinates: Coordinates;
  formattedAddress: string;
  placeId?: string;
}

export interface GeocodeRequest {
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode?: string;
}

class LocationService {
  private googleMapsApiKey: string;
  private geocoder: google.maps.Geocoder | null = null;
  private placesService: google.maps.places.PlacesService | null = null;

  constructor() {
    this.googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

    // Log Google Maps API key status for debugging
    if (!this.googleMapsApiKey) {
      console.warn(
        '⚠️ Google Maps API key not configured. Please set VITE_GOOGLE_MAPS_API_KEY in your .env file'
      );
    } else if (this.googleMapsApiKey.includes('Your_Google_Maps_API_Key_Here')) {
      console.warn(
        '⚠️ Google Maps API key appears to be a placeholder. Please set a real API key in VITE_GOOGLE_MAPS_API_KEY'
      );
      this.googleMapsApiKey = ''; // Clear placeholder key
    }
  }

  /**
   * Initialize Google Maps services
   */
  private async initializeGoogleMaps(): Promise<void> {
    if (!this.googleMapsApiKey) {
      throw new Error('Google Maps API key is not configured');
    }

    // Load Google Maps API if not already loaded
    if (!window.google?.maps) {
      await this.loadGoogleMapsScript();
    }

    if (!this.geocoder) {
      this.geocoder = new google.maps.Geocoder();
    }

    if (!this.placesService) {
      // Create a dummy map element for Places service
      const mapDiv = document.createElement('div');
      const map = new google.maps.Map(mapDiv);
      this.placesService = new google.maps.places.PlacesService(map);
    }
  }

  /**
   * Load Google Maps JavaScript API
   */
  private loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, _reject) => {
      if (window.google?.maps) {
        resolve();
        return;
      }

      if (!this.googleMapsApiKey) {
        _reject(
          new Error(
            'Google Maps API key is not configured. Please set VITE_GOOGLE_MAPS_API_KEY in your .env file'
          )
        );
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;

      script.onload = () => resolve();
      script.onerror = () =>
        _reject(
          new Error(
            'Failed to load Google Maps API. Please check your API key and ensure it has the Places API enabled.'
          )
        );

      document.head.appendChild(script);
    });
  }

  /**
   * Geocode an address to get coordinates
   */
  async geocodeAddress(request: GeocodeRequest): Promise<GeocodeResult | null> {
    try {
      await this.initializeGoogleMaps();

      if (!this.geocoder) {
        throw new Error('Geocoder not initialized');
      }

      // Build formatted address string
      const addressParts = [
        request.address,
        request.city,
        request.state,
        request.zipCode,
        request.country,
      ].filter(Boolean);

      const fullAddress = addressParts.join(', ');

      return new Promise((resolve, _reject) => {
        this.geocoder!.geocode({ address: fullAddress }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
            const result = results[0];
            const location = result.geometry.location;

            resolve({
              coordinates: {
                lat: location.lat(),
                lng: location.lng(),
              },
              formattedAddress: result.formatted_address,
              placeId: result.place_id,
            });
          } else {
            console.warn('Geocoding failed:', status, fullAddress);
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error('Error geocoding address:', error);
      return null;
    }
  }

  /**
   * Get place details by place ID
   */
  async getPlaceDetails(placeId: string): Promise<google.maps.places.PlaceResult | null> {
    try {
      await this.initializeGoogleMaps();

      if (!this.placesService) {
        throw new Error('Places service not initialized');
      }

      return new Promise((resolve, _reject) => {
        this.placesService!.getDetails(
          {
            placeId,
            fields: ['geometry', 'formatted_address', 'name', 'types'],
          },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place) {
              resolve(place);
            } else {
              console.warn('Place details request failed:', status);
              resolve(null);
            }
          }
        );
      });
    } catch (error) {
      console.error('Error getting place details:', error);
      return null;
    }
  }

  /**
   * Search for places with autocomplete
   */
  async searchPlaces(query: string): Promise<google.maps.places.AutocompletePrediction[]> {
    try {
      await this.initializeGoogleMaps();

      const service = new google.maps.places.AutocompleteService();

      return new Promise((resolve, _reject) => {
        service.getPlacePredictions(
          {
            input: query,
            types: ['geocode'],
          },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              resolve(predictions);
            } else {
              resolve([]);
            }
          }
        );
      });
    } catch (error) {
      console.error('Error searching places:', error);
      return [];
    }
  }

  /**
   * Validate if Google Maps API is available
   */
  isGoogleMapsAvailable(): boolean {
    return (
      !!this.googleMapsApiKey &&
      this.googleMapsApiKey.length > 0 &&
      !this.googleMapsApiKey.includes('Your_Google_Maps_API_Key_Here')
    );
  }

  /**
   * Get current user location (if permission granted)
   */
  async getCurrentLocation(): Promise<Coordinates | null> {
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        position => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        error => {
          console.warn('Geolocation error:', error);
          resolve(null);
        },
        {
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }
}

// Export singleton instance
export const locationService = new LocationService();

// TypeScript declarations for Google Maps
declare global {
  interface Window {
    google: typeof google;
  }
}
